import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ArrowLeft, Trash, PencilSimple, FloppyDisk, X } from "@phosphor-icons/react";
import ReportButton from "@/components/ReportButton";

export default function TopicDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [topic, setTopic] = useState(null);
  const [content, setContent] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function load() {
    try {
      const { data } = await api.get(`/community/topics/${id}`);
      setTopic(data);
    } catch (err) {
      toast.error("Tópico não encontrado");
      navigate("/comunidade");
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  async function postComment(e) {
    e.preventDefault();
    if (!user) { toast.error("Entra para comentar"); navigate("/login"); return; }
    if (!content.trim()) return;
    setSubmitting(true);
    try {
      await api.post(`/community/topics/${id}/comments`, { content });
      setContent("");
      load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  }

  async function deleteTopic() {
    if (!confirm("Apagar tópico?")) return;
    try {
      await api.delete(`/community/topics/${id}`);
      toast.success("Tópico apagado");
      navigate("/comunidade");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  }

  async function editTopic() {
    const newTitle = window.prompt("Editar título:", topic.title);
    if (newTitle == null) return;
    const newContent = window.prompt("Editar conteúdo:", topic.content);
    if (newContent == null) return;
    try {
      await api.put(`/community/topics/${id}`, { title: newTitle.trim() || topic.title, content: newContent });
      toast.success("Tópico atualizado");
      load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  }

  if (!topic) return <div className="pt-32 text-center text-zinc-500 font-mono-accent text-xs">A CARREGAR...</div>;

  const canDelete = user && (user.user_id === topic.author_id || user.role === "admin");

  return (
    <div className="pt-20 max-w-3xl mx-auto px-5 lg:px-8 pb-24" data-testid="topic-detail-page">
      <Link to="/comunidade" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white mb-6">
        <ArrowLeft size={16} weight="bold" /> Voltar à comunidade
      </Link>

      <div className="bg-[#121217] border border-white/5 rounded-2xl p-6 lg:p-8">
        <div className="flex items-center gap-2 mb-3">
          <span className="tag">{topic.category}</span>
          <span className="font-mono-accent text-[10px] text-zinc-500">{new Date(topic.created_at).toLocaleString("pt-PT")}</span>
          <div className="ml-auto flex gap-2 items-center">
            {user && user.user_id !== topic.author_id && (
              <ReportButton entityType="topic" entityId={topic.topic_id} size={16} />
            )}
            {canDelete && (
              <>
                <button onClick={editTopic} className="text-zinc-500 hover:text-[#7209B7]" data-testid="edit-topic-btn" title="Editar">
                  <PencilSimple size={16} weight="bold" />
                </button>
                <button onClick={deleteTopic} className="text-zinc-500 hover:text-[#E63946]" data-testid="delete-topic-btn">
                  <Trash size={16} weight="bold" />
                </button>
              </>
            )}
          </div>
        </div>
        <h1 className="font-display text-3xl lg:text-4xl font-bold tracking-tight">{topic.title}</h1>
        <Link to={`/membros/${topic.author_id}`} className="flex items-center gap-3 mt-4 hover:opacity-80 transition-opacity">
          <Avatar user={{ name: topic.author_name, picture: topic.author_picture }} />
          <div className="font-display font-semibold">{topic.author_name}</div>
        </Link>
        <div className="mt-6 text-zinc-300 leading-relaxed whitespace-pre-line">{topic.content}</div>
      </div>

      <div className="mt-10">
        <div className="font-mono-accent text-xs text-zinc-500 mb-4">RESPOSTAS · {topic.comments.length}</div>
        <div className="space-y-4">
          {topic.comments.map(c => (
            <div key={c.comment_id} className="bg-[#121217] border border-white/5 rounded-xl p-5" data-testid={`comment-${c.comment_id}`}>
              <div className="flex items-center gap-3 mb-3">
                <Link to={`/membros/${c.author_id}`}><Avatar user={{ name: c.author_name, picture: c.author_picture }} /></Link>
                <div className="flex-1">
                  <Link to={`/membros/${c.author_id}`} className="font-display font-semibold text-sm hover:text-[#E63946] transition-colors">{c.author_name}</Link>
                  <div className="font-mono-accent text-[10px] text-zinc-500">{new Date(c.created_at).toLocaleString("pt-PT")} {c.edited_at && "· editado"}</div>
                </div>
                {user && (user.user_id === c.author_id || user.role === "admin") ? (
                  <CommentActions comment={c} onChange={load} />
                ) : user ? (
                  <ReportButton entityType="comment" entityId={c.comment_id} />
                ) : null}
              </div>
              <div className="text-zinc-300 whitespace-pre-line leading-relaxed">{c.content}</div>
            </div>
          ))}
        </div>

        <form onSubmit={postComment} className="mt-6 bg-[#121217] border border-white/5 rounded-xl p-5">
          <Textarea
            placeholder={user ? "Escreve a tua resposta..." : "Entra para comentar"}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            rows={4}
            className="bg-[#1C1C22] border-white/10"
            data-testid="comment-input"
          />
          <div className="mt-3 flex justify-end">
            <Button type="submit" disabled={submitting || !content.trim()} className="btn-crimson" data-testid="comment-submit-btn">
              {submitting ? "A enviar..." : "Responder"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Avatar({ user }) {
  if (user.picture) {
    return <img src={user.picture} alt="" className="w-9 h-9 rounded-full object-cover" />;
  }
  return (
    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#E63946] to-[#7209B7] grid place-items-center text-sm font-bold">
      {user.name?.[0]?.toUpperCase() || "?"}
    </div>
  );
}

function CommentActions({ comment, onChange }) {
  async function edit() {
    const next = window.prompt("Editar comentário:", comment.content);
    if (next == null || next.trim() === comment.content) return;
    if (!next.trim()) return;
    try {
      await api.put(`/community/comments/${comment.comment_id}`, { content: next.trim() });
      toast.success("Comentário atualizado");
      onChange();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  }
  async function remove() {
    if (!confirm("Apagar comentário?")) return;
    try {
      await api.delete(`/community/comments/${comment.comment_id}`);
      toast.success("Comentário apagado");
      onChange();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  }
  return (
    <div className="flex gap-1">
      <button onClick={edit} className="text-zinc-500 hover:text-[#7209B7] p-1.5" data-testid={`comment-edit-${comment.comment_id}`} title="Editar"><PencilSimple size={14} weight="bold" /></button>
      <button onClick={remove} className="text-zinc-500 hover:text-[#E63946] p-1.5" data-testid={`comment-delete-${comment.comment_id}`} title="Apagar"><Trash size={14} weight="bold" /></button>
    </div>
  );
}
